import React, { useState } from "react"
import {
  Box,
  Button,
  Flex,
  Text,
  Input,
  Alert,
  AlertIcon,
  Spinner,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react"
import axios from "axios"
import { useSupplierRetailer } from "../../wrappers/SupplierRetailerContext"

const OrdersUpload = ({ onUploadComplete }) => {
  const { selectedSupplier } = useSupplierRetailer()
  const [file, setFile] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [result, setResult] = useState(null)
  const [error, setError] = useState(null)
  const toast = useToast()

  const cardBg = useColorModeValue("white", "gray.800")
  const borderColor = useColorModeValue("gray.200", "gray.700")

  const handleFileChange = (e) => {
    const selected = e.target.files[0]
    if (selected && !selected.name.endsWith(".csv")) {
      setError("Please select a CSV file")
      setFile(null)
      return
    }
    setFile(selected)
    setError(null)
    setResult(null)
  }

  const handleUpload = async () => {
    if (!file) {
      setError("Please select a file first")
      return
    }
    if (!selectedSupplier) {
      setError("Please select a supplier first")
      return
    }

    setUploading(true)
    setError(null)
    setResult(null)

    try {
      const formData = new FormData()
      formData.append("csvFile", file)
      formData.append("supplier", selectedSupplier)
      formData.append("userid", localStorage.getItem("userid"))

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/orders/upload`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      )

      if (response.data.status === 1) {
        setResult(response.data)
        toast({
          title: "Upload Successful",
          description: response.data.message,
          status: "success",
          duration: 3000,
        })
        if (onUploadComplete && response.data.data?.upload_id) {
          onUploadComplete(response.data.data.upload_id)
        }
      } else {
        setError(response.data.message || "Upload failed")
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Upload failed")
    } finally {
      setUploading(false)
    }
  }

  return (
    <Box>
      <Box
        bg={cardBg}
        border="1px"
        borderColor={borderColor}
        borderRadius="lg"
        p={6}
        maxW="600px"
        mx="auto"
      >
        <Text fontSize="lg" fontWeight="bold" mb={4}>
          Upload PNP Orders CSV
        </Text>

        <Text fontSize="sm" color="gray.500" mb={4}>
          Supplier: <strong>{selectedSupplier || "Not selected"}</strong>
        </Text>

        <Box mb={4}>
          <Input
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            p={1}
            border="2px dashed"
            borderColor={borderColor}
            borderRadius="md"
            h="auto"
            py={4}
            cursor="pointer"
          />
        </Box>

        {file && (
          <Text fontSize="sm" mb={4} color="gray.600">
            Selected: {file.name} ({(file.size / 1024).toFixed(1)} KB)
          </Text>
        )}

        {error && (
          <Alert status="error" borderRadius="md" mb={4}>
            <AlertIcon />
            {error}
          </Alert>
        )}

        {result && (
          <Alert status="success" borderRadius="md" mb={4}>
            <AlertIcon />
            {result.message}
          </Alert>
        )}

        <Flex justify="flex-end">
          <Button
            colorScheme="blue"
            onClick={handleUpload}
            isDisabled={!file || uploading || !selectedSupplier}
          >
            {uploading ? <Spinner size="sm" mr={2} /> : null}
            {uploading ? "Uploading..." : "Upload Orders"}
          </Button>
        </Flex>
      </Box>
    </Box>
  )
}

export default OrdersUpload
